<template>
  <!-- 易宝账户信息 -->
  <div ref="registrInfoRef">
    <!-- 营业执照信息 -->
    <div class="manage-card-box">
      <div class="info-card-title">
        营业执照基本信息
      </div>
      <div class="base-info-list">
        <div class="left-list gap-y-[14px]">
          <div class="item">
            <div class="label">
              企业名称
            </div>
            <div class="value">
              {{ data.name }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              注册手机号
            </div>
            <div class="value">
              {{ data.contactsPhone }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              统一社会信用代码
            </div>
            <div class="value">
              {{ data.creditCode }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              企业地址
            </div>
            <div class="value">
              {{ data.address }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              注册邮箱
            </div>
            <div class="value">
              {{ data.contactsEmail }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              经营范围
            </div>
            <div class="value">
              {{ data.businessScope }}
            </div>
          </div>
        </div>
        <div class="right-list">
          <div class="item">
            <div class="label">
              营业执照照片
            </div>
            <div class="value">
              <div class="business-license-box">
                <img :src="data.businessLicensePic" alt="">
              </div>
            </div>
          </div>
          <div class="item">
            <div class="label">
              营业执照有效期
            </div>
            <div class="value">
              <span>{{ changeFormatToDate(data.businessLicenseStartDate) }}</span>
              <span> ~ </span>
              <span>{{ changeFormatToDate(data.businessLicenseEndDate) }}&nbsp;&nbsp;</span>
              <span>{{ isBusinessLongTime ? '长期' : '' }}</span>
            </div>
          </div>

          <div v-if="!isCompany" class="item">
            <div class="label">
              证件签发日
            </div>
            <div class="value">
              {{ changeFormatToDate(data.businessLicenseStartDate) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 法人信息 -->
    <div class="manage-card-box my-5">
      <div class="info-card-title">
        法人信息
      </div>
      <div class="base-info-list">
        <div class="left-list gap-y-[14px]">
          <div class="item">
            <div class="label">
              法定代表人姓名
            </div>
            <div class="value">
              {{ data.legalName }}
            </div>
          </div>
          <div v-if="source!='agent'" class="item">
            <div class="label">
              法定代表人电话
            </div>
            <div class="value">
              {{ data.legalPhone }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              身份证人像面
            </div>
            <div class="value">
              <div class="id-card-box">
                <a>
                  <img :src="data.idNoFrontUrl" alt="">
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="right-list">
          <div class="item">
            <div class="label">
              法定代表人证件号
            </div>
            <div class="value">
              {{ data.legalIdNo }}
            </div>
          </div>
          <div v-if="source!='agent'" class="item">
            <div class="label">
              证件有效期
            </div>
            <div class="value">
              <span>{{ changeFormatToDate(data.idNoExpiryStartDate) }}</span>
              <span> ~ </span>
              <span>{{ changeFormatToDate(data.idNoExpiryEndDate) }}&nbsp;&nbsp;</span>
              <span>{{ isLongTime ? '长期' : '' }}</span>
            </div>
          </div>
          <div class="item">
            <div class="label">
              身份证国徽面
            </div>
            <div class="value">
              <div class="id-card-box">
                <a>
                  <img :src="data.idNoBackUrl" alt="">
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 开户绑卡信息 -->
    <div class="manage-card-box my-5">
      <div class="info-card-title">
        开户绑卡信息
      </div>
      <div class="base-info-list">
        <div class="left-list gap-y-[14px]">
          <div class="item">
            <div class="label">
              银行卡号
            </div>
            <div class="value">
              {{ data.bankCardNo }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              开户银行
            </div>
            <div class="value">
              {{ basicBank.bankName }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              对公账户行名
            </div>
            <div class="value">
              {{ data.bankBranch }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              对公账户行号
            </div>
            <div class="value">
              {{ data.cnapsCode }}
            </div>
          </div>
          <div class="item">
            <div class="label">
              对公账户名
            </div>
            <div class="value">
              {{ data.bankAccountName }}
            </div>
          </div>
        </div>
        <div class="right-list">
          <div class="item">
            <div class="label">
              银行开户许可证
            </div>
            <div class="value">
              <div class="business-license-box">
                <a>
                  <img :src="data.bankOpeningLicense" alt="">
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { changeFormatToDate } from '@core/tools';
import { computed, PropType, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useElementImgPreview } from '@vue3/components';
import { IMasterBank, IYibaoDetail } from '../../../../types/yibao';
import { ECompanyType } from '../../../../types/common';

const props = defineProps({
  data: {
    type: Object as PropType<IYibaoDetail>,
    default() {
      return {} as IYibaoDetail;
    },
  },
  basicBankList: {
    type: Array<IMasterBank>,
    default() {
      return [] as IMasterBank[];
    },
  },
});

defineOptions({ name: 'YibaoAccountInfo' });

const route = useRoute();
const info = ref({} as any);
const registrInfoRef = ref<HTMLDivElement>();

const source = computed(() => route.query.from);
const storeType = computed(() => route.query.storeType as ECompanyType);
const isCompany = computed(() => storeType.value === ECompanyType.Company);
const isBusinessLongTime = computed(() => info.value.businessLicenseEndDate && ['29991231', '2999-12-31'].includes(props.data.businessLicenseEndDate));
const isLongTime = computed(() => info.value.idNoExpiryEndDate && ['29991231', '2999-12-31'].includes(props.data.idNoExpiryEndDate));

const basicBank = computed(() => props.basicBankList.find((item) => item.bankCode === props.data?.bankCode) || { bankName: '' });

useElementImgPreview(registrInfoRef);

</script>
